import ClientLogsService from "@src/services/client-logs.service";
import { unfreezeAppointment } from "@src/services/unfreeze-appointment.service";
import logger from "@src/utils/logger";
import { isValidPositiveNumber } from "@src/utils/number-utils";
import type { FC, ReactNode } from "react";
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useApplicationContext } from "./ApplicationContext";
import { useEnvironment } from "./EnvironmentContext";

// Create Timer Context
const TimerContext = createContext<
  | {
      timeLeft: number;
      resetTimer: () => void;
      startTimer: () => void;
      startTimerWithServerTimestamp: (frozenAtTimestamp: string) => void;
      stopTimer: () => void;
      isTimerRunning: boolean;
      showTimeoutModal: boolean;
      setShowTimeoutModal: (value: boolean) => void;
      handleSessionTimeout: () => Promise<void>;
    }
  | undefined
>(undefined);

export const TimerProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [fallBackTimerDuration, setFallbackTimerDuration] = useState<number>(1200); // 1200 seconds / 20 minutes for testing
  const { data, setData, clearCompletedRoutes, clearAppointmentSelection } =
    useApplicationContext();
  const { TIMER_INITIAL_VALUE } = useEnvironment();
  const [timeLeft, setTimeLeft] = useState<number>(fallBackTimerDuration);

  const resetTimer = useCallback(() => {
    setTimeLeft(fallBackTimerDuration);
    // Clear relevant session data from localStorage
    localStorage.removeItem("timeLeft");
    localStorage.removeItem("timerTimestamp");
    localStorage.removeItem("isTimerRunning");
  }, [fallBackTimerDuration]);

  const startTimer = () => {
    logger.log({
      text: "Timer started at",
      value: new Date().toISOString(),
      location: "TimerContext.startTimer",
    });
    setIsTimerRunning(true);
    localStorage.setItem("timeLeft", timeLeft.toString());
    localStorage.setItem("isTimerRunning", "true");
    localStorage.setItem("timerTimestamp", Date.now().toString());
  };

  /**
   * Start timer with server-provided freeze timestamp
   * Calculates elapsed time and adjusts timer accordingly for accurate synchronization
   * @param frozenAtTimestamp - ISO 8601 timestamp from server when appointment was frozen
   */
  const startTimerWithServerTimestamp = (frozenAtTimestamp: string) => {
    try {
      const serverFreezeTime = new Date(frozenAtTimestamp).getTime();
      if (!Number.isFinite(serverFreezeTime)) {
        throw new Error("Invalid frozenAt timestamp");
      }
      const clientCurrentTime = Date.now();
      const elapsedSeconds = Math.floor((clientCurrentTime - serverFreezeTime) / 1000);
      const adjustedTimeLeft = Math.max(fallBackTimerDuration - elapsedSeconds, 0);

      logger.log({
        text: "Timer started with server timestamp",
        value: frozenAtTimestamp,
        location: "TimerContext.startTimerWithServerTimestamp",
      });

      // Set the adjusted time before starting
      setTimeLeft(adjustedTimeLeft);
      setIsTimerRunning(true);
      localStorage.setItem("timeLeft", adjustedTimeLeft.toString());
      localStorage.setItem("isTimerRunning", "true");
      localStorage.setItem("timerTimestamp", Date.now().toString());
    } catch (error) {
      ClientLogsService.logError(
        data.userUuid || "unknown",
        "TimerContext.startTimerWithServerTimestamp",
        "Failed to parse frozenAt timestamp, falling back to standard startTimer",
        error instanceof Error ? error : undefined,
      );
      startTimer();
    }
  };

  const stopTimer = useCallback(() => {
    logger.log({
      text: "Timer ended at",
      value: new Date().toISOString(),
      location: "TimerContext.stopTimer",
    });
    setIsTimerRunning(false); // Stop the timer when it reaches 0
    localStorage.setItem("isTimerRunning", "false");
    resetTimer(); // Reset timer to original value
  }, [resetTimer]);

  const handleSessionTimeout = async () => {
    // Display timeout modal
    ClientLogsService.logWebAction(
      data.userUuid || "unknown",
      "TimerContext.handleSessionTimeout",
      "Session has timed out. Showing timeout modal.",
    );
    setShowTimeoutModal(true);

    // Call the unfreezeAppointment function
    if (data.userUuid) {
      await unfreezeAppointment(data.userUuid);
    } else {
      // Clear some application context data
      setData("userUuid", null);
      setData("selectedPolicy", null);
      setData("insurancePolicy", null);
      setData("paymentMethod", null);
      setData("insurancePolicies", null);
      setData("providerList", null);
      setData("selectedProvider", null);
      clearAppointmentSelection();
      clearCompletedRoutes();
      ClientLogsService.logError(
        "unknown",
        "TimerContext.handleSessionTimeout",
        "No userUuid found in application context data to unfreeze appointment.",
      );
    }
    clearAppointmentSelection();
  };

  useEffect(() => {
    const savedTime = localStorage.getItem("timeLeft");
    const savedTimestamp = localStorage.getItem("timerTimestamp");
    const currentTime = Date.now();

    if (savedTime && savedTimestamp) {
      const elapsedSeconds = Math.floor((currentTime - parseInt(savedTimestamp, 10)) / 1000);
      const remainingTime = parseInt(savedTime, 10) - elapsedSeconds;
      setTimeLeft(remainingTime > 0 ? remainingTime : 0); // Ensure timeLeft is not negative
      return;
    }

    const initialValueToUse = isValidPositiveNumber(TIMER_INITIAL_VALUE)
      ? TIMER_INITIAL_VALUE
      : fallBackTimerDuration;

    setTimeLeft(initialValueToUse);
    setFallbackTimerDuration(initialValueToUse);
  }, [TIMER_INITIAL_VALUE, fallBackTimerDuration]);

  const [isTimerRunning, setIsTimerRunning] = useState(() => {
    // Restore the timer running state from localStorage
    return localStorage.getItem("isTimerRunning") === "true";
  });

  const [showTimeoutModal, setShowTimeoutModal] = useState(false);

  useEffect(() => {
    if (!isTimerRunning) return;

    // Save the timer state to localStorage whenever it changes
    localStorage.setItem("timeLeft", timeLeft.toString());
    localStorage.setItem("timerTimestamp", Date.now().toString());
    localStorage.setItem("isTimerRunning", "true");

    if (timeLeft <= 0) {
      stopTimer();
      ClientLogsService.logWebAction(
        data.userUuid || "unknown",
        "TimerProvider",
        "Session has timed out. Showing timeout modal.",
      );
      setShowTimeoutModal(true);
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, [timeLeft, isTimerRunning, resetTimer]);

  // Handle tab visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && isTimerRunning) {
        // Recalculate the time left when the user returns to the tab
        const savedTime = localStorage.getItem("timeLeft");
        const savedTimestamp = localStorage.getItem("timerTimestamp");
        const currentTime = Date.now();

        if (savedTime && savedTimestamp) {
          const elapsedSeconds = Math.floor((currentTime - parseInt(savedTimestamp, 10)) / 1000);
          const remainingTime = parseInt(savedTime, 10) - elapsedSeconds;
          setTimeLeft(remainingTime > 0 ? remainingTime : 0);
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isTimerRunning]);

  return (
    <TimerContext.Provider
      value={{
        timeLeft,
        resetTimer,
        startTimer,
        startTimerWithServerTimestamp,
        stopTimer,
        isTimerRunning,
        showTimeoutModal,
        setShowTimeoutModal,
        handleSessionTimeout,
      }}
    >
      {children}
    </TimerContext.Provider>
  );
};

export const useTimerContext = () => {
  const context = useContext(TimerContext);
  if (!context) {
    throw new Error("useTimerContext must be used within a TimerProvider");
  }
  return context;
};
